/**
 * Every type the AutoAware backend sends or accepts, in one place.
 *
 * The previous frontend declared `Telemetry` three times and `User` without the
 * `phone` field it actually read, which shipped because the build never ran tsc.
 * Nothing here is generated: `/api/v1/openapi.json` is only reachable from a
 * running backend, and the surface is small enough to keep honest by hand.
 *
 * Where a comment says "not exposed", the omission is deliberate on the backend
 * side — do not add the field back to make a UI easier.
 */

export const ROLES = [
  'vehicle_owner',
  'fleet_manager',
  'emergency_responder',
  'administrator',
] as const;
export type Role = (typeof ROLES)[number];

export const SEVERITIES = ['normal', 'minor', 'major', 'critical'] as const;
/** `normal` never produces a CrashEvent, so it never reaches a list view. */
export type Severity = (typeof SEVERITIES)[number];
export type DispatchedSeverity = Exclude<Severity, 'normal'>;

export type AlertType = 'SMS' | 'call' | 'push' | 'dashboard';
export type AlertStatus = 'pending' | 'sent' | 'failed';

export type DeviceState = 'unclaimed' | 'claimed' | 'revoked' | 'rma' | 'locked';

/* -------------------------------------------------------------------------- */
/* Auth                                                                        */
/* -------------------------------------------------------------------------- */

export interface User {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  /** Present on UserOut. Treated as a credential — never rendered. */
  push_token: string | null;
  /** Next-of-kin notified alongside the owner on a crash. */
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  role: Role;
  created_at: string;
  /** Privacy-notice version this user accepted; null if they never have. */
  consent_version: string | null;
}

/** Registration takes no role. The backend always creates a vehicle_owner (H2). */
export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  phone?: string;
  /** Explicit acceptance of the privacy notice. The server rejects false. */
  consent: boolean;
}

export interface PrivacyNotice {
  version: string;
  controller: string;
  grievance_email: string | null;
  data_collected: string[];
  purposes: string[];
  recipients: string[];
  retention: string[];
  rights: string[];
}

export interface ConsentStatus {
  given: boolean;
  version: string | null;
  current_version: string;
  up_to_date: boolean;
  consent_at: string | null;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

/**
 * What /auth/login answers with. Either a session or a challenge, never both.
 *
 * An administrator's password is only half their credential, so they get
 * `mfa_required` and a short-lived challenge token instead of a session. The
 * challenge is accepted by the 2FA endpoints alone — never by /auth/me.
 */
export interface LoginResult {
  access_token: string | null;
  token_type: string;
  mfa_required: boolean;
  /** The account must enrol in an authenticator before it can sign in at all. */
  enrollment_required: boolean;
  challenge_token: string | null;
}

/** `otpauth_uri` is what the QR encodes; `secret` is the same value to type by hand. */
export interface MfaSetup {
  secret: string;
  otpauth_uri: string;
}

/** Recovery codes are returned exactly once and stored only as hashes. */
export interface MfaEnabled {
  recovery_codes: string[];
  access_token: string;
  token_type: string;
}

export interface MfaStatus {
  enabled: boolean;
  recovery_codes_remaining: number;
}

export interface UserUpdatePayload {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  push_token?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
}

/* -------------------------------------------------------------------------- */
/* Vehicles                                                                    */
/* -------------------------------------------------------------------------- */

export interface Vehicle {
  id: number;
  license_plate: string;
  vehicle_type: string;
  owner_id: number;
}

export interface VehicleCreatePayload {
  license_plate: string;
  vehicle_type: string;
}

/* -------------------------------------------------------------------------- */
/* Devices                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Note the two identifiers. `id` is the integer primary key used by detail,
 * telemetry and WebSocket routes; `device_uid` is the opaque string used by
 * claim, unclaim, revoke and RMA. `api/endpoints/devices.ts` is the only place
 * allowed to know which is which.
 */
export interface Device {
  id: number;
  device_uid: string | null;
  serial_number: string;
  firmware_version: string | null;
  /** Resolved through the open device_ownership row, not a column. */
  vehicle_id: number | null;
  state: DeviceState;
  last_seen: string | null;
  created_at: string;
}

export interface DeviceStatus {
  id: number;
  serial_number: string;
  last_seen: string | null;
  /** Seen within the last 5 minutes, computed server-side. */
  is_online: boolean;
  gps_lat: number | null;
  gps_lon: number | null;
}

export interface DeviceClaimPayload {
  device_uid: string;
  claim_code: string;
  vehicle_id?: number;
}

/**
 * The rotated claim code is returned exactly once and is unrecoverable.
 * `new_claim_code` is null when the transition deliberately did not rotate — see
 * restoring a locked device, where the owner still holds a valid printed code.
 */
export interface DeviceUnclaimed {
  device_uid: string;
  state: DeviceState;
  new_claim_code: string | null;
}

/** `hw_batch` is the manufacturing run. Required by the API, not optional. */
export interface DeviceProvisionPayload {
  count: number;
  hw_batch: string;
  sim_iccids?: string[];
  public_keys?: string[];
}

/**
 * The administrator registry view. Everything `Device` has, plus manufacturing and
 * owner metadata. Served only by GET /devices/admin — the fleet-wide GET /devices a
 * fleet manager also reads deliberately omits owner name and email.
 *
 * There is no credential here and there never will be: the secret is stored
 * encrypted and the claim code only as a hash.
 */
export interface DeviceAdmin extends Device {
  hw_batch: string | null;
  sim_iccid: string | null;
  claim_attempts: number;
  locked_until: string | null;
  is_online: boolean;
  owner_id: number | null;
  owner_name: string | null;
  owner_email: string | null;
  claimed_at: string | null;
}

export interface DeviceAdminFilters {
  state?: DeviceState | undefined;
  q?: string | undefined;
}

/** The only response in the system carrying plaintext credentials. Show once. */
export interface DeviceProvisioned {
  device_uid: string;
  /** null for secure-element units, which hold no server-side secret. */
  secret: string | null;
  claim_code: string;
}

/* -------------------------------------------------------------------------- */
/* Telemetry                                                                   */
/* -------------------------------------------------------------------------- */

export interface Telemetry {
  id: number;
  device_id: number;
  timestamp: string;
  accel_x: number;
  accel_y: number;
  accel_z: number;
  gyro_x: number;
  gyro_y: number;
  gyro_z: number;
  gps_lat: number | null;
  gps_lon: number | null;
  speed: number | null;
}

/** Server-side validation bounds, reused for chart domains and form limits. */
export const TELEMETRY_BOUNDS = {
  accel: 2000,
  gyro: 35,
  speedMax: 500,
} as const;

/** What arrives on the WebSocket: a telemetry row, or a keepalive to ignore. */
export type LiveMessage = Telemetry | { type: 'ping' };

export function isPing(msg: LiveMessage): msg is { type: 'ping' } {
  return 'type' in msg && msg.type === 'ping';
}

/* -------------------------------------------------------------------------- */
/* Crashes and alerts                                                          */
/* -------------------------------------------------------------------------- */

/**
 * There is no vehicle, owner or address on this payload, and no crash detail
 * endpoint. `features/crashes/useCrashDetail.ts` joins it client-side.
 */
/**
 * pending  — inside the cancel window; the owner can still stop the dispatch
 * confirmed — the window elapsed without a cancel (treated as a real crash)
 * cancelled — the owner said "I'm OK"
 * auto_dismissed — passed the physical gate but scored 'normal' (not shown as a crash)
 */
export type CrashStatus = 'pending' | 'confirmed' | 'cancelled' | 'auto_dismissed';

export interface CrashEvent {
  id: number;
  device_id: number;
  timestamp: string;
  severity: DispatchedSeverity;
  confidence: number;
  gps_lat: number | null;
  gps_lon: number | null;
  status?: CrashStatus;
  /** `rules:v1` or `rf:<trained_at>:<hash>` — which scorer produced this verdict. */
  scoring_model_version?: string | null;
}

/**
 * `target` — the phone number or FCM token an alert was sent to — is deliberately
 * not exposed by the API. It is PII, and there must be no UI column for it.
 */
export interface Alert {
  id: number;
  crash_event_id: number;
  alert_type: AlertType;
  status: AlertStatus;
  attempts: number;
  /** Up to 2000 chars. The single most useful field when a dispatch fails. */
  last_error: string | null;
  /** Set at row creation, overwritten on success; on a failed row it means
   *  "last attempted at", not "delivered at". */
  sent_at: string | null;
}

/* -------------------------------------------------------------------------- */
/* Operations console (administrator only)                                     */
/* -------------------------------------------------------------------------- */

export interface StateCount {
  state: string;
  count: number;
}

export interface BatchCount {
  hw_batch: string | null;
  total: number;
  claimed: number;
  /** Provisioned and never once seen — still on a shelf, lost, or dead on arrival. */
  never_reported: number;
}

/** Product-level health. Nothing here is scoped to a caller's own vehicles. */
export interface AdminStats {
  devices_total: number;
  devices_by_state: StateCount[];
  devices_never_reported: number;
  /** Claimed and quiet over 24h — the owner believes they are covered and is not. */
  devices_silent_24h: number;
  batches: BatchCount[];
  users_total: number;
  users_by_role: StateCount[];
  users_without_emergency_contact: number;
  vehicles_total: number;
  crashes_7d: number;
  crashes_30d: number;
  alerts_failed_7d: number;
  alerts_pending: number;
  dispatch_emergency_services: boolean;
  scoring_is_rule_based: boolean;
}

export interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  role: Role;
  created_at: string;
  has_emergency_contact: boolean;
  vehicle_count: number;
  device_count: number;
}

export interface CustomerDevice {
  id: number;
  device_uid: string | null;
  serial_number: string;
  state: DeviceState;
  is_online: boolean;
  last_seen: string | null;
  claimed_at: string | null;
  vehicle_id: number | null;
  hw_batch: string | null;
}

export interface CustomerCrash {
  id: number;
  device_id: number;
  timestamp: string;
  severity: DispatchedSeverity;
  alerts_total: number;
  alerts_failed: number;
}

/** Support's single screen. No telemetry and no coordinates, deliberately. */
export interface CustomerDetail {
  customer: Customer;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  vehicles: Vehicle[];
  devices: CustomerDevice[];
  recent_crashes: CustomerCrash[];
}

export interface DashboardSummary {
  total_vehicles: number;
  total_devices: number;
  total_crashes: number;
  active_alerts: number;
}
